import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetMetadocSearchIndexStatusQuery,
  useSearchMetadocMutation,
  useStartMetadocSearchIndexMutation,
} from "services/metadoc-search-api";
import {
  getRequestedMetadocSearchVersionSuffix,
  resolveMetadocSearchVersionContext,
} from "./metadocSearchVersioning";

import type { GroupingSource } from "services/metadoc-api";
import type {
  MetadocSearchIndexStatus,
  MetadocSearchResult,
} from "services/metadoc-search-api";

type UseMetadocSearchParams = {
  metadocId: number;
  onPageChange: (page: number) => void;
  groupingSource: GroupingSource;
  hasPreAiRunDownloadUrl: boolean;
};

const SEARCH_INDEX_POLL_INTERVAL_MS = 3000;
const SEARCH_RESULTS_PAGE_SIZE = 99;

/*
 * manages metadoc search indexing, query execution,
 * result navigation, and active-page highlights.
 */
export const useMetadocSearch = ({
  metadocId,
  onPageChange,
  groupingSource,
  hasPreAiRunDownloadUrl,
}: UseMetadocSearchParams) => {
  const [searchResults, setSearchResults] = useState<MetadocSearchResult[]>([]);
  const [searchTotalHits, setSearchTotalHits] = useState(0);
  const [activeSearchResultIndex, setActiveSearchResultIndex] = useState<
    number | null
  >(null);
  const [lastSearchQuery, setLastSearchQuery] = useState("");

  const [searchMetadoc, { isLoading: isSearching }] =
    useSearchMetadocMutation();
  const [startIndexing, { isLoading: isStartingIndexing }] =
    useStartMetadocSearchIndexMutation();

  const {
    data: searchIndexStatusResponse,
    isFetching: isSearchIndexFetching,
    refetch: refetchSearchIndexStatus,
  } = useGetMetadocSearchIndexStatusQuery(
    { metadocId },
    {
      skip: !metadocId,
      pollingInterval: 0,
    },
  );

  const requestedVersionSuffix = useMemo(
    () =>
      getRequestedMetadocSearchVersionSuffix({
        groupingSource,
        hasPreAiRunDownloadUrl,
      }),
    [groupingSource, hasPreAiRunDownloadUrl],
  );

  const { resolvedVersionSuffix, statusVersionSuffix } = useMemo(
    () =>
      resolveMetadocSearchVersionContext({
        requestedVersionSuffix,
        versions: searchIndexStatusResponse?.versions ?? [],
      }),
    [requestedVersionSuffix, searchIndexStatusResponse?.versions],
  );

  const activeVersionStatus = useMemo(() => {
    return searchIndexStatusResponse?.versions.find(
      (version) => version.version_suffix === statusVersionSuffix,
    );
  }, [searchIndexStatusResponse?.versions, statusVersionSuffix]);

  const searchIndexStatus = useMemo<
    MetadocSearchIndexStatus | undefined
  >(() => {
    if (!searchIndexStatusResponse) {
      return undefined;
    }

    if (!activeVersionStatus) {
      return {
        metadoc_id: searchIndexStatusResponse.metadoc_id,
        metadoc_name: searchIndexStatusResponse.metadoc_name,
        status: "not_indexed",
        opensearch_document_id: null,
        total_pages_indexed: null,
        total_text_blocks_indexed: null,
        error_message: null,
        indexing_started_at: null,
        indexing_completed_at: null,
        is_searchable: false,
      };
    }

    return {
      metadoc_id: searchIndexStatusResponse.metadoc_id,
      metadoc_name: searchIndexStatusResponse.metadoc_name,
      status: activeVersionStatus.status,
      opensearch_document_id: activeVersionStatus.opensearch_document_id,
      total_pages_indexed: activeVersionStatus.total_pages_indexed,
      total_text_blocks_indexed: activeVersionStatus.total_text_blocks_indexed,
      error_message: activeVersionStatus.error_message,
      indexing_started_at: activeVersionStatus.indexing_started_at,
      indexing_completed_at: activeVersionStatus.indexing_completed_at,
      is_searchable: activeVersionStatus.is_searchable,
    };
  }, [activeVersionStatus, searchIndexStatusResponse]);

  const shouldPollSearchIndex =
    activeVersionStatus?.status === "pending" ||
    activeVersionStatus?.status === "processing" ||
    isStartingIndexing;

  useEffect(() => {
    if (!shouldPollSearchIndex) {
      return;
    }

    const interval = window.setInterval(() => {
      refetchSearchIndexStatus();
    }, SEARCH_INDEX_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(interval);
    };
  }, [refetchSearchIndexStatus, shouldPollSearchIndex]);

  useEffect(() => {
    setSearchResults([]);
    setSearchTotalHits(0);
    setActiveSearchResultIndex(null);
    setLastSearchQuery("");
  }, [metadocId, resolvedVersionSuffix]);

  const goToSearchResult = useCallback(
    (resultIndex: number) => {
      const result = searchResults[resultIndex];
      if (!result) {
        return;
      }

      setActiveSearchResultIndex(resultIndex);
      onPageChange(result.page_number);
    },
    [onPageChange, searchResults],
  );

  const handlePreviousSearchResult = useCallback(() => {
    if (searchResults.length === 0) {
      return;
    }

    if (activeSearchResultIndex === null) {
      goToSearchResult(0);
      return;
    }

    const previousResultIndex =
      activeSearchResultIndex === 0
        ? searchResults.length - 1
        : activeSearchResultIndex - 1;

    goToSearchResult(previousResultIndex);
  }, [activeSearchResultIndex, goToSearchResult, searchResults.length]);

  const handleNextSearchResult = useCallback(() => {
    if (searchResults.length === 0) {
      return;
    }

    if (activeSearchResultIndex === null) {
      goToSearchResult(0);
      return;
    }

    const nextResultIndex =
      activeSearchResultIndex === searchResults.length - 1
        ? 0
        : activeSearchResultIndex + 1;

    goToSearchResult(nextResultIndex);
  }, [activeSearchResultIndex, goToSearchResult, searchResults.length]);

  const canGoToPreviousSearchResult = searchResults.length > 1;
  const canGoToNextSearchResult = searchResults.length > 1;

  const isSearchReady =
    activeVersionStatus?.status === "completed" &&
    activeVersionStatus.is_searchable;

  const handleStartIndexing = useCallback(async () => {
    try {
      // requestedVersionSuffix rather than resolvedVersionSuffix: the latter is
      // undefined while falling back to a legacy unscoped index, and indexing
      // the requested version is what retires that fallback.
      await startIndexing({
        metadocId,
        versionSuffix: requestedVersionSuffix,
      }).unwrap();
      refetchSearchIndexStatus();
    } catch (error) {
      // error is handled by rtkQueryErrorLogger middleware
    }
  }, [
    metadocId,
    refetchSearchIndexStatus,
    requestedVersionSuffix,
    startIndexing,
  ]);

  const handleSearch = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();

      if (!trimmedQuery) {
        setSearchResults([]);
        setSearchTotalHits(0);
        setActiveSearchResultIndex(null);
        setLastSearchQuery("");
        return;
      }

      if (!isSearchReady) {
        KaboodleToaster.error("Search index is not ready yet.");
        return;
      }

      try {
        const response = await searchMetadoc({
          metadocId,
          query: trimmedQuery,
          page: 1,
          pageSize: SEARCH_RESULTS_PAGE_SIZE,
          versionSuffix: resolvedVersionSuffix,
        }).unwrap();

        setSearchResults(response.results);
        setSearchTotalHits(response.total_hits);
        setLastSearchQuery(trimmedQuery);

        if (response.results.length === 0) {
          setActiveSearchResultIndex(null);
          return;
        }

        setActiveSearchResultIndex(0);
        onPageChange(response.results[0].page_number);
      } catch (error) {
        KaboodleToaster.error(error);
      }
    },
    [
      isSearchReady,
      metadocId,
      onPageChange,
      resolvedVersionSuffix,
      searchMetadoc,
    ],
  );

  const highlightsByPage = useMemo(() => {
    const map = new Map<number, MetadocSearchResult[]>();
    if (activeSearchResultIndex === null) {
      return map;
    }

    const activeSearchResult = searchResults[activeSearchResultIndex];
    if (!activeSearchResult) {
      return map;
    }

    map.set(activeSearchResult.page_number, [activeSearchResult]);
    return map;
  }, [activeSearchResultIndex, searchResults]);

  return {
    activeSearchResultIndex,
    canGoToNextSearchResult,
    canGoToPreviousSearchResult,
    handleNextSearchResult,
    handlePreviousSearchResult,
    handleSearch,
    handleStartIndexing,
    highlightsByPage,
    isSearchIndexFetching,
    isSearchReady,
    isSearching,
    isStartingIndexing,
    lastSearchQuery,
    searchIndexStatus,
    searchNavigableResultCount: searchResults.length,
    searchResultCount: searchTotalHits,
  };
};
